import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useMemo, useRef, useState } from 'react';
import 'dayjs/locale/ko';
import { notificationApi } from '../../services/notificationApi';
import type { Notification, NotificationListResponse } from '../../types/notification';

// dayjs 설정
dayjs.extend(relativeTime);
dayjs.locale('ko');

export default function NotificationDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('right');
  const queryClient = useQueryClient();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 배지용 첫 페이지 데이터 조회 (항상 실행)
  const { data: badgeData } = useQuery<NotificationListResponse>({
    queryKey: ['notifications-badge'],
    queryFn: () =>
      notificationApi.getList({
        per_page: 1,
        page: 1,
        load_more: true,
      }),
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 60_000, // 1분마다 자동 갱신
    refetchOnWindowFocus: true,
  });

  // 알림 목록 조회 (더보기 모드 - 드롭다운 열 때만)
  const {
    data: notificationData,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<NotificationListResponse>({
    queryKey: ['notifications-dropdown', page],
    queryFn: () =>
      notificationApi.getList({
        per_page: 10,
        page,
        load_more: true,
      }),
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: isOpen,
  });

  // 드롭다운 위치 계산
  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 361; // sm:w-[361px]
      const windowWidth = window.innerWidth;
      const padding = 16;

      // 모바일 (640px 미만)
      if (windowWidth < 640) {
        // 중앙 정렬로 처리됨 (Tailwind 클래스에서)
        return;
      }

      // 오른쪽 공간 확인
      const spaceOnRight = windowWidth - buttonRect.right;

      // 오른쪽 공간이 부족하면 왼쪽 정렬
      if (spaceOnRight < dropdownWidth + padding) {
        setDropdownPosition('left');
      } else {
        setDropdownPosition('right');
      }
    }
  }, [isOpen]);

  // 윈도우 리사이즈 시 드롭다운 닫기
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // 모든 페이지의 알림 데이터를 캐시에서 가져와서 합치기
  const allNotifications = useMemo(() => {
    const notifications: Notification[] = [];

    for (let i = 1; i <= page; i++) {
      const cachedData = queryClient.getQueryData<NotificationListResponse>(['notifications-dropdown', i]);

      if (cachedData?.data?.list) {
        notifications.push(...cachedData.data.list);
      }
    }

    const uniqueNotifications = Array.from(new Map(notifications.map((n) => [n.id, n])).values());

    return uniqueNotifications;
  }, [page, queryClient]); // ✅ 수정됨

  // 배지 데이터 사용 (드롭다운 닫혀있을 때)
  const displayData = isOpen ? notificationData : badgeData;

  // 알림 데이터 추출
  const totalUnread = displayData?.data?.summary?.total_unread || 0;
  const totalCount = displayData?.data?.summary?.total || 0;
  const hasMore = notificationData?.data?.pagination?.has_more || false;
  const loadedCount = notificationData?.data?.pagination?.loaded || allNotifications.length;

  // 알림 읽음 처리 Mutation
  const markAsAdminReadMutation = useMutation({
    mutationFn: (id: number) => notificationApi.markAsAdminRead(id),
    onSuccess: (data, variables) => {
      console.log('🚀 ~ NotificationDropdown ~ data:', data);
      const notification = allNotifications.find((n) => n.id === variables);
      const wasUnreadByDealer = notification && !notification.is_read;

      if (wasUnreadByDealer) {
        queryClient.setQueryData<NotificationListResponse>(['notifications-badge'], (old) => {
          if (!old) return old;
          return {
            ...old,
            data: {
              ...old.data,
              summary: {
                ...old.data.summary,
                total_unread: Math.max(0, old.data.summary.total_unread - 1),
              },
            },
          };
        });
      }

      for (let i = 1; i <= page; i++) {
        queryClient.setQueryData<NotificationListResponse>(['notifications-dropdown', i], (old) => {
          if (!old) return old;

          return {
            ...old,
            data: {
              ...old.data,
              list: old.data.list.map((n) =>
                n.id === variables
                  ? {
                      ...n,
                      is_read_by_admin: true,
                      admin_read_at: new Date().toISOString(),
                    }
                  : n,
              ),
              summary:
                wasUnreadByDealer && i === 1
                  ? {
                      ...old.data.summary,
                      total_unread: Math.max(0, old.data.summary.total_unread - 1),
                    }
                  : old.data.summary,
            },
          };
        });
      }
    },
  });

  // 드롭다운 열릴 때 배경 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setPage(1);
    }
  };

  const handleClick = () => {
    toggleDropdown();
  };

  const handleLoadMore = () => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  // 전화번호 마스킹 함수 (마지막 4자리만 표시)
  const maskPhoneNumber = (phone: string | null): string => {
    if (!phone) return '';
    if (phone.length <= 4) return phone;
    const lastFour = phone.slice(-4);
    return `***-****-${lastFour}`;
  };

  // 알림 타입에 따른 아이콘 반환
  const getNotificationIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'point_used':
        return (
          <div className='flex items-center justify-center w-10 h-10 bg-orange-100 rounded-full dark:bg-orange-900/20'>
            <svg
              className='w-5 h-5 text-orange-600 dark:text-orange-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
        );
      case 'point_charged':
        return (
          <div className='flex items-center justify-center w-10 h-10 bg-green-100 rounded-full dark:bg-green-900/20'>
            <svg
              className='w-5 h-5 text-green-600 dark:text-green-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
        );
      case 'point_cancelled':
        return (
          <div className='flex items-center justify-center w-10 h-10 bg-red-100 rounded-full dark:bg-red-900/20'>
            <svg
              className='w-5 h-5 text-red-600 dark:text-red-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </div>
        );
      case 'dealer_registered':
        return (
          <div className='flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full dark:bg-blue-900/20'>
            <svg
              className='w-5 h-5 text-blue-600 dark:text-blue-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className='flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full dark:bg-gray-700/20'>
            <svg
              className='w-5 h-5 text-gray-600 dark:text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
              />
            </svg>
          </div>
        );
    }
  };

  const getRelativeTime = (dateString: string) => {
    return dayjs(dateString).fromNow();
  };

  // 알림 아이템 클릭 핸들러
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read_by_admin) {
      markAsAdminReadMutation.mutate(notification.id);
    }
  };

  return (
    <div className='relative'>
      <button
        ref={buttonRef}
        className='relative flex items-center justify-center text-gray-500 transition-colors bg-white border border-gray-200 rounded-full dropdown-toggle hover:text-gray-700 h-11 w-11 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white'
        onClick={handleClick}>
        {totalUnread > 0 && (
          <span className='absolute right-0 top-0.5 z-10 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-orange-500 rounded-full'>
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
        <svg className='fill-current' width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M10.75 2.29248C10.75 1.87827 10.4143 1.54248 10 1.54248C9.58583 1.54248 9.25004 1.87827 9.25004 2.29248V2.83613C6.08266 3.20733 3.62504 5.9004 3.62504 9.16748V14.4591H3.33337C2.91916 14.4591 2.58337 14.7949 2.58337 15.2091C2.58337 15.6234 2.91916 15.9591 3.33337 15.9591H4.37504H15.625H16.6667C17.0809 15.9591 17.4167 15.6234 17.4167 15.2091C17.4167 14.7949 17.0809 14.4591 16.6667 14.4591H16.375V9.16748C16.375 5.9004 13.9174 3.20733 10.75 2.83613V2.29248ZM14.875 14.4591V9.16748C14.875 6.47509 12.6924 4.29248 10 4.29248C7.30765 4.29248 5.12504 6.47509 5.12504 9.16748V14.4591H14.875ZM8.00004 17.7085C8.00004 18.1228 8.33583 18.4585 8.75004 18.4585H11.25C11.6643 18.4585 12 18.1228 12 17.7085C12 17.2943 11.6643 16.9585 11.25 16.9585H8.75004C8.33583 16.9585 8.00004 17.2943 8.00004 17.7085Z'
            fill='currentColor'
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* 모바일 오버레이 배경 */}
          <div className='fixed inset-0 bg-black/30 z-40 backdrop-blur-none ' />

          {/* 드롭다운 */}
          <div
            ref={dropdownRef}
            className={`
              fixed sm:absolute
              top-1/2 sm:top-full
              left-1/2 sm:left-auto
              -translate-x-1/2 sm:translate-x-0
              -translate-y-1/2 sm:translate-y-0
              sm:mt-[10px]
              flex h-[480px] w-[calc(100vw-2rem)] sm:w-[350px] lg:w-[361px]
              flex-col rounded-2xl  border border-gray-300 dark:border-gray-600 p-3
               dark:bg-gray-dark bg-white
              z-150
              max-w-[361px]
              shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_10px_10px_-5px_rgba(0,0,0,0.04)]
              dark:shadow-[0_20px_25px_-5px_rgba(0,0,0,0.4),0_10px_10px_-5px_rgba(0,0,0,0.2)]
              ${dropdownPosition === 'left' ? 'sm:right-0' : 'sm:left-0'}
            `}>
            <div className='flex items-center justify-between pb-3 mb-3 border-b border-gray-100 dark:border-gray-700'>
              <div className='flex flex-col'>
                <h5 className='text-md font-semibold text-gray-800 dark:text-gray-200'>알림</h5>
                {totalCount > 0 && (
                  <span className='text-xs text-gray-500 dark:text-gray-400'>
                    {totalUnread > 0 ? `읽지 않음 ${totalUnread}개` : '모두 읽음'} · 전체 {totalCount}개
                  </span>
                )}
              </div>
              <button
                onClick={toggleDropdown}
                className='text-gray-500 transition dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'>
                <svg
                  className='fill-current'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  xmlns='http://www.w3.org/2000/svg'>
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z'
                    fill='currentColor'
                  />
                </svg>
              </button>
            </div>

            {isLoading && page === 1 ? (
              <div className='flex flex-col items-center justify-center flex-1 text-center'>
                <div className='w-8 h-8 mb-4 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
                <p className='text-sm text-gray-500 dark:text-gray-400'>알림을 불러오는 중...</p>
              </div>
            ) : isError ? (
              <div className='flex flex-col items-center justify-center flex-1 text-center'>
                <svg
                  className='w-16 h-16 mb-4 text-red-300 dark:text-red-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <p className='mb-2 text-sm font-medium text-gray-700 dark:text-gray-300'>알림을 불러올 수 없습니다</p>
                <button onClick={() => refetch()} className='text-sm text-blue-600 hover:underline dark:text-blue-400'>
                  다시 시도
                </button>
              </div>
            ) : allNotifications.length === 0 ? (
              <div className='flex flex-col items-center justify-center flex-1 text-center'>
                <svg
                  className='w-16 h-16 mb-4 text-gray-300 dark:text-gray-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
                  />
                </svg>
                <p className='text-sm text-gray-500 dark:text-gray-400'>새로운 알림이 없습니다</p>
              </div>
            ) : (
              <>
                <ul className='flex flex-col flex-1 overflow-y-auto custom-scrollbar gap-2'>
                  {allNotifications.map((notification) => (
                    <li key={notification.id}>
                      <button
                        onClick={() => handleNotificationClick(notification)}
                        className={`w-full flex gap-3 rounded-lg border-b border-gray-100 p-3 px-4.5 py-3 hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-white/5 transition-colors ${
                          !notification.is_read_by_admin
                            ? 'bg-blue-50/50 dark:bg-blue-900/10'
                            : 'bg-gray-50/50 dark:bg-gray-100/3'
                        }`}>
                        {getNotificationIcon(notification.type)}

                        <div className='flex-1 min-w-0 text-left'>
                          <div className='flex items-start justify-between gap-2 mb-1'>
                            <div className='flex flex-col gap-0.5'>
                              <span className='text-sm font-medium text-gray-800 dark:text-white/90'>
                                {notification.title}
                              </span>
                              {notification.dealer_name && notification.type !== 'dealer_registered' && (
                                <div className='flex items-center gap-1.5 text-xs'>
                                  <span className='text-gray-600 dark:text-gray-400'>
                                    {notification.dealer_name} 딜러
                                  </span>
                                  {notification.customer_phone && (
                                    <>
                                      <span className='w-1 h-1 bg-gray-400 rounded-full'></span>
                                      <span className='text-gray-500 dark:text-gray-500 font-mono'>
                                        {maskPhoneNumber(notification.customer_phone)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            {!notification.is_read_by_admin && (
                              <span className='flex-shrink-0 w-2 h-2 mt-1.5 bg-blue-500 rounded-full'></span>
                            )}
                          </div>

                          <p className='mb-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2'>
                            {notification.content}
                          </p>

                          <div className='flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500'>
                            <span>{getRelativeTime(notification.created_at)}</span>
                            {notification.data?.points_used && (
                              <>
                                <span className='w-1 h-1 bg-gray-400 rounded-full'></span>
                                <span className='font-medium text-orange-600 dark:text-orange-400'>
                                  -{notification.data.points_used}P
                                </span>
                              </>
                            )}
                            {notification.data?.point_amount && (
                              <>
                                <span className='w-1 h-1 bg-gray-400 rounded-full'></span>
                                <span className='font-medium text-green-600 dark:text-green-400'>
                                  +{notification.data.point_amount.toLocaleString()}P
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>

                {hasMore && (
                  <button
                    onClick={handleLoadMore}
                    disabled={isFetching}
                    className='flex items-center justify-center gap-2 px-4 py-2 mt-3 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'>
                    {isFetching ? (
                      <>
                        <div className='w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin' />
                        <span>불러오는 중...</span>
                      </>
                    ) : (
                      <>
                        <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                        </svg>
                        <span>
                          더보기 ({loadedCount}/{totalCount})
                        </span>
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
